// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, createContext, useContext, useMemo, useState, useSyncExternalStore } from 'react';

import type { DeepReadonly } from '@evolvemep/api-ts';

import { createAPSViewerStore } from 'components/Autodesk/extensions/APSViewerStoreExtension/createAPSViewerStore';
import { createDimensioningStore } from 'components/Autodesk/extensions/DimensioningExtension/createDimensioningStore';
import { createElementTagsStore } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/createElementTagsStore';
import { createViewerMarkupStore } from 'components/Autodesk/extensions/MarkupModeExtension/createViewerMarkupStore';
import { createSpoolsStore } from 'components/Autodesk/extensions/SpoolsExtension/createSpoolsStore';
import { isNil } from 'helpers/isNotNil';

/**
 *  The minimum shape every registered store must expose
 *  Includes the functions required by `useSyncExternalStore`, and an additional `clear` function that the extension
 *  can call while unloading to reset the store to its initial state.
 **/
export type AutodeskStoreBase<T> = {
  getSnapshot: () => T;
  subscribe: (listener: () => void) => () => void;
  clear: () => void;
};

/**
 * Any store creator registrable below. The `opts` parameter is typed `never` so that creators
 * taking no options, optional options, or required options are all assignable (via parameter
 * contravariance); each key's real options type is recovered by `OptsFor`.
 */
type AutodeskStoreCreator<T> = (opts?: never) => AutodeskStoreBase<T>;

/**
 * Pairs a store creator with its own React context, so every registered store gets an isolated
 * context instance. The context is deliberately typed as `AutodeskStoreBase` — the provider and
 * hooks below narrow values back to the key's concrete store type, which sidesteps the variance
 * problems of casting `Context<T>` itself.
 */
const defineExtensionStore = <T, Creator extends AutodeskStoreCreator<T>>(createStore: Creator) => ({
  createStore,
  StoreContext: createContext<AutodeskStoreBase<T> | undefined>(undefined),
});

/**
 * Registry of all stores used by Autodesk extensions. Each member provides a store creator and its own React context.
 */
export const autodeskExtensionStores = {
  apsViewer: defineExtensionStore(createAPSViewerStore),
  dimensioning: defineExtensionStore(createDimensioningStore),
  elementTags: defineExtensionStore(createElementTagsStore),
  spools: defineExtensionStore(createSpoolsStore),
  viewerMarkup: defineExtensionStore(createViewerMarkupStore),
} as const satisfies Record<string, ReturnType<typeof defineExtensionStore>>;

type AutodeskExtensionStores = typeof autodeskExtensionStores;

export type AutodeskExtensionStoreName = keyof AutodeskExtensionStores;

export type StoreFor<K extends AutodeskExtensionStoreName> = ReturnType<AutodeskExtensionStores[K]['createStore']>;

type SnapshotFor<K extends AutodeskExtensionStoreName> = DeepReadonly<ReturnType<StoreFor<K>['getSnapshot']>>;

/**
 * The initialization options accepted by the key's store creator. Indexing the parameters tuple
 * with `number` (rather than `0`) resolves to `never` for creators that take no options, instead
 * of being a type error.
 */
type OptsFor<K extends AutodeskExtensionStoreName> = Parameters<AutodeskExtensionStores[K]['createStore']>[number];

/**
 * Provides an isolated instance of the named store to all children, so multiple simultaneous
 * viewers never share or overwrite each other's state. The store is created once on mount;
 * `storeName` and `opts` must not change for the lifetime of the provider.
 */
export const AutodeskExtensionStoreProvider = <K extends AutodeskExtensionStoreName>({
  children,
  storeName,
  opts,
}: {
  children: ReactNode;
  storeName: K;
  /** Initialization options for the stores whose creator accepts them. */
  opts?: OptsFor<K>;
}) => {
  const { createStore, StoreContext } = autodeskExtensionStores[storeName];
  const [store] = useState(() => createStore(opts));

  return <StoreContext value={store}>{children}</StoreContext>;
};

/**
 * Returns the named store and throws an error if it's not present.
 * Used internally by {@link useAutodeskExtensionStoreSnapshot} and {@link useAutodeskExtensionStoreActions}.
 */
const useAutodeskExtensionStore = <K extends AutodeskExtensionStoreName>(storeName: K): StoreFor<K> => {
  // Safe: `AutodeskExtensionStoreProvider` only ever populates this key's context with this key's store.
  const store = useContext(autodeskExtensionStores[storeName].StoreContext) as StoreFor<K> | undefined;
  if (isNil(store)) {
    throw new Error(`useAutodeskExtensionStore must be used within a "${storeName}" AutodeskExtensionStoreProvider`);
  }
  return store;
};

/** Returns the latest snapshot of the named store — use this in components that render store state. */
export const useAutodeskExtensionStoreSnapshot = <K extends AutodeskExtensionStoreName>(
  storeName: K,
): SnapshotFor<K> => {
  const store = useAutodeskExtensionStore(storeName);
  // Safe: `getSnapshot` comes from the same store the declared return type is derived from.
  return useSyncExternalStore(store.subscribe, store.getSnapshot as () => SnapshotFor<K>);
};

/** Returns the named store's action methods, without the `useSyncExternalStore` plumbing. */
export const useAutodeskExtensionStoreActions = <K extends AutodeskExtensionStoreName>(
  storeName: K,
): Omit<StoreFor<K>, keyof AutodeskStoreBase<never>> => {
  const store = useAutodeskExtensionStore(storeName);
  return useMemo(() => {
    const { getSnapshot: _, subscribe: __, clear: ___, ...actions } = store;
    return actions;
  }, [store]);
};
